import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import * as yup from "yup";

import { IPortalPreferences } from "../../../../@types/Preferences";
import { Button } from "../../../../components/Button";
import { Dropdown, DropdownTextItem } from "../../../../components/Dropdown";
import { TextField } from "../../../../components/TextField";
import { ToastNotification } from "../../../../components/ToastNotification";
import { AUTH_PORTAL_PREFERENCES } from "../../../../constants/localStorageKeys";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { userService } from "../../../../services/userService";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../../../utils/storage";
import {
  ButtonContainer,
  CancelButton,
  CodeContainer,
  CodeSubtitle,
  CodeTitle,
  InputClass,
  ModalContainer,
  ModalTitle,
  NoteContainer,
  NoteSubtitle,
  NoteTitle,
  ResendCodeHyperlink,
  TypeContainer,
  TypeSelectContainer,
  TypeTitle,
  reactPhoneInputCss,
} from "./SecurityModal.styles";
import "react-phone-input-2/lib/bootstrap.css";

interface ISecurityModalProps {
  setIsMfaEnabled: any;
  onClose: () => void;
  email: string;
}

export function SecurityModal(props: ISecurityModalProps) {
  const { t } = useTranslation();

  const { setIsMfaEnabled, onClose, email } = props;
  const { closeModal } = useModal();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [newOtpSent, setNewOtpSent] = useState(false);
  const [apiError, setApiError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [mfaType, setMfaType] = useState("Email");
  const { isMobile } = useIsMobile();
  const [phoneWithCountryCode, setPhoneWithCountryCode] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>();

  useEffect(() => {
    const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);

    if (portalPreferences) {
      const parsedPortalPreferences = safeJsonParse<
        IPortalPreferences | undefined
      >(portalPreferences, undefined);

      if (parsedPortalPreferences) {
        setCountryCode(parsedPortalPreferences.countryCode?.toLowerCase());
      }
    }
  });

  const sendOtpValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("login_validation_emailInvalid"))
      .required(t("login_validation_emailRequired")),
  });

  const isSendingOtpRef = useRef(false);
  const isCheckingOtpRef = useRef(false);

  const handleSendOtpSubmitForm = async ({ email, phoneNumber }: any) => {
    if (isSendingOtpRef.current) return;
    isSendingOtpRef.current = true;

    try {
      mixpanelActions.track("User Action: MFAModalConfirm");

      setSubmitLoading(true);

      const enableMfaResponse = await userService.enableUserMfa({
        email,
        phoneNumber,
        mfaType,
      });

      if (enableMfaResponse.status === 400) {
        setApiError(enableMfaResponse.data);
      }

      if (enableMfaResponse.status === 200) {
        setApiError("");
        setOtpSent(true);
      }

      setSubmitLoading(false);
    } finally {
      isSendingOtpRef.current = false;
    }
  };

  const sendOtpFormik = useFormik({
    initialValues: {
      email,
      phoneNumber: "",
    },
    onSubmit: handleSendOtpSubmitForm,
    validationSchema: sendOtpValidationSchema,
  });

  const shouldShowErrorMessage = (field: "email" | "phoneNumber") => {
    if (apiError !== "") {
      return apiError;
    }
    return sendOtpFormik.touched[field] ? sendOtpFormik.errors[field] : "";
  };
  const handleOTPChange = (e: any) => {
    const regex = /^[0-9\b]+$/;
    if (e.target.value === "" || regex.test(e.target.value)) {
      setOtp(e.target.value);
    }
  };

  const checkOTPValidation = async () => {
    if (isCheckingOtpRef.current) return;
    isCheckingOtpRef.current = true;

    try {
      setSubmitLoading(true);
      mixpanelActions.track("User Action: MFAModalConfirm");
      const request = {
        email: sendOtpFormik.values.email,
        code: otp,
        mfaType,
      };

      const confirmMfaResponse = await userService.confirmEnableUserMfa(
        request
      );

      if (confirmMfaResponse.status === 200) {
        if (confirmMfaResponse.data.length > 1) {
          setOtpError(confirmMfaResponse.data);
        } else {
          setIsMfaEnabled(true);
          onClose();
          closeModal();
        }
      }

      setSubmitLoading(false);
    } finally {
      isCheckingOtpRef.current = false;
    }
  };

  const resendOtp = async () => {
    if (!newOtpSent) {
      await userService.resendOtp({
        email: sendOtpFormik.values.email,
        phoneNumber: sendOtpFormik.values.phoneNumber,
        mfaType,
      });
      setNewOtpSent(true);
      setTimeout(() => {
        setNewOtpSent(false);
      }, 3500);
    }
  };

  const handleCancelClick = () => {
    mixpanelActions.track("User Action: MFAModalCancel");
    closeModal();
  };

  const handleButtonDisabled = () => {
    if (!otpSent) {
      if (
        (mfaType.toLowerCase() === "sms" &&
          sendOtpFormik.values.phoneNumber === "") ||
        (mfaType.toLowerCase() === "email" && sendOtpFormik.values.email === "")
      ) {
        return true;
      }
    }

    if (otpSent && otp.length !== 6) {
      return true;
    }

    return false;
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter" && !handleButtonDisabled()) {
      if (!otpSent) {
        sendOtpFormik.submitForm();
      } else {
        checkOTPValidation();
      }
    }
  };

  const handleInputChange = (event: any) => {
    setApiError("");
    sendOtpFormik.handleChange(event);
  };

  const handlePhoneInput = (country: any, formattedValue: string) => {
    const formattedPhoneNumber = formattedValue.replace(/ /g, "");
    const formattedDialCode = `+${country.dialCode}`;

    if (formattedPhoneNumber !== formattedDialCode) {
      sendOtpFormik.setFieldValue("phoneNumber", formattedPhoneNumber);
    } else {
      sendOtpFormik.setFieldValue("phoneNumber", "");
    }

    setPhoneWithCountryCode(formattedValue);
  };

  const handleCountryChange = () => {
    const inputElement = document.getElementById("phone-input");
    if (inputElement) {
      inputElement.focus();
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>{t("settings_securityModal_title")}</ModalTitle>
      {!otpSent ? (
        <div>
          <TypeContainer>
            <TypeSelectContainer>
              <TypeTitle>{t("settings_securityModal_type")}</TypeTitle>
              <Dropdown
                trigger={t(
                  mfaType === "SMS"
                    ? "settings_securityModal_sms"
                    : "settings_securityModal_email"
                )}
                onValueChange={(e) => {
                  setMfaType(e);
                }}
                backgroundColor="#E6E6E6"
                fontColor="#212121"
                size="small"
              >
                <DropdownTextItem
                  text={t("settings_securityModal_email")}
                  value="Email"
                />
                <DropdownTextItem
                  text={t("settings_securityModal_sms")}
                  value="SMS"
                />
              </Dropdown>
            </TypeSelectContainer>
            {mfaType === "Email" ? (
              <TextField
                name="email"
                label=""
                value={sendOtpFormik.values.email}
                onChange={(event) => handleInputChange(event)}
                className={InputClass()}
                placeholder={t("settings_securityModal_emailPlaceholder")}
                errorMessage={shouldShowErrorMessage("email")}
                disabled={email !== ""}
                backgroudColor="white"
              />
            ) : (
              <PhoneInput
                inputProps={{
                  autoFocus: true,
                  id: "phone-input",
                }}
                searchPlaceholder={t("settings_securityModal_search")}
                country={countryCode?.toLowerCase()}
                containerClass={reactPhoneInputCss()}
                enableSearch
                value={phoneWithCountryCode}
                onChange={(value, country, e, formattedValue) => {
                  handleCountryChange();
                  handlePhoneInput(country, formattedValue);
                }}
                countryCodeEditable={false}
              />
            )}
          </TypeContainer>
          <NoteContainer>
            <NoteTitle>{t("settings_securityModal_noteTitle")}</NoteTitle>
            <NoteSubtitle>
              {mfaType === "Email"
                ? t("settings_securityModal_noteEmail")
                : t("settings_securityModal_noteSms")}
            </NoteSubtitle>
          </NoteContainer>
        </div>
      ) : (
        <CodeContainer>
          <CodeTitle>{t("settings_securityModal_enterCode")}</CodeTitle>
          <TextField
            name="confirmationCode"
            label=""
            value={otp}
            onChange={handleOTPChange}
            onKeyDown={handleKeyDown}
            className={InputClass()}
            placeholder="00-00-00"
            errorMessage={otpError}
            backgroudColor="white"
            maxLength={6}
          />
          <div>
            <CodeSubtitle>
              <Trans
                i18nKey="settings_securityModal_resendCode"
                components={{
                  resend: <ResendCodeHyperlink onClick={resendOtp} />,
                }}
              />
            </CodeSubtitle>
          </div>
        </CodeContainer>
      )}
      <ButtonContainer>
        <Button
          onClick={!otpSent ? sendOtpFormik.submitForm : checkOTPValidation}
          type="submit"
          label={t("settings_securityModal_continue")}
          disabled={handleButtonDisabled()}
          isLoading={submitLoading}
          size={isMobile ? "small" : undefined}
        />
        <CancelButton onClick={handleCancelClick}>
          {t("common_cancel")}
        </CancelButton>
      </ButtonContainer>
      {newOtpSent && (
        <ToastNotification
          message={t("settings_securityModal_codeSent")}
          type="success"
          width="190px"
          rightMargin="0"
        />
      )}
    </ModalContainer>
  );
}
